import type { ComponentType } from 'react'

/**
 * Client-side module registry.
 *
 * Mirrors the backend module registry but on the frontend side.
 * Each module registers its lazy-loaded React component here.
 *
 * The server tells us WHICH modules are active (via GET /api/platform/modules).
 * The client registry tells us HOW to render each active module (the component).
 *
 * At startup, the app computes the intersection of:
 *   - Server registry (what's enabled by config)
 *   - Client registry (what's implemented in code)
 *
 * This means you can enable a module on the server before the frontend
 * component is ready — it simply won't appear in navigation (graceful degradation).
 */

export interface ClientModule {
  id: string
  /** Lazy-loaded route component — only downloaded when the module is accessed */
  Component: ComponentType
}

/** Registry of all implemented frontend modules */
const registry: Map<string, ClientModule> = new Map()

/**
 * Registers a module's component with the client registry.
 * Call this from each module's index.ts file.
 *
 * Example (in frontend/src/modules/todo/index.ts):
 *   registerModule({
 *     id: 'todo',
 *     Component: lazy(() => import('./TodoModule')),
 *   })
 */
export function registerModule(module: ClientModule): void {
  if (registry.has(module.id)) {
    console.warn(`[ModuleRegistry] Duplicate module registration: "${module.id}"`)
    return
  }
  registry.set(module.id, module)
}

/** Returns the registered client module for the given ID, or undefined */
export function getModule(id: string): ClientModule | undefined {
  return registry.get(id)
}

/** Returns all registered client module IDs */
export function getRegisteredIds(): string[] {
  return Array.from(registry.keys())
}

// ── Module Registrations ──────────────────────────────────────
// Register all implemented modules here.
// Each module is lazy-loaded — only downloaded when the user navigates to it.

import { lazyWithRetry } from '@/shared/utils/lazyWithRetry'

registerModule({
  id: 'dashboard',
  Component: lazyWithRetry(() => import('@/features/dashboard/DashboardPage')),
})

registerModule({
  id: 'goals',
  Component: lazyWithRetry(() => import('@/features/goals/GoalsPage')),
})

registerModule({
  id: 'planner',
  Component: lazyWithRetry(() => import('@/features/planner/PlannerPage')),
})

registerModule({
  id: 'calendar',
  Component: lazyWithRetry(() => import('@/features/calendar/CalendarPage')),
})

registerModule({
  id: 'gita',
  Component: lazyWithRetry(() => import('@/features/gita/GitaModule')),
})

registerModule({
  id: 'french',
  Component: lazyWithRetry(() => import('@/features/french/FrenchModule')),
})

registerModule({
  id: 'language-goals',
  Component: lazyWithRetry(() => import('@/features/languagegoal/LanguageGoalPage')),
})

registerModule({
  id: 'learning',
  Component: lazyWithRetry(() => import('@/features/learning/LearningHubPage')),
})

registerModule({
  id: 'plan',
  Component: lazyWithRetry(() => import('@/features/planner/PlanHubPage')),
})

registerModule({
  id: 'more',
  Component: lazyWithRetry(() => import('@/platform/shell/MorePage')),
})

registerModule({
  id: 'crs',
  Component: lazyWithRetry(() => import('@/features/crs/CrsCalculatorPage')),
})



